import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { Box, Divider, Paper, styled, Tab, Theme } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import { getDenthubSettingsTemplate } from 'apollo/templates/denthub-settings';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import AppBannerSettings from 'components/denthub-settings/app-banner-settings.component';
import ApiClientSettings from 'components/api-client-settings/api-client-settings.component';
import DbSwitchSettings from 'components/denthub-settings/db-switch-settings.component';
import IntegrationApiModeSettings from 'components/denthub-settings/integration-api-mode-settings.component';
import Loader from 'components/shared/loader/loader.component';
import { DenthubSetting } from 'models/denthub-settings';
import { DB_SWITCH_ENABLED } from 'context/data-set-switch';
import CrediflowPartyLookup from 'components/app-settings/crediflow-party-lookup.component';
import OAuthApps from 'components/app-settings/oauth-apps/oauth-apps.component';

const AppSettings: FC = () => {
    const { t: translate } = useTranslation(['common', 'app-settings']);
    const { tab } = useParams<{ tab: string }>();
    const [tabState, setTabState] = useState('app-banner');
    const navigate = useNavigate();
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const { data, loading } = useQuery<{ denthubSettings: DenthubSetting }>(getDenthubSettingsTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });
    const denthubSettings = data?.denthubSettings;

    useEffect(() => {
        setTabState(tab || 'app-banner');
    }, [tab]);

    const handleTabChange = (_: unknown, newTabState: string) => {
        if (tabState !== newTabState) {
            navigate(`/app-settings/${newTabState}`);
            setTabState(newTabState);
        }
    };

    const handleSettingsUpdated = () => {
        setAppToasterContent({
            isOpen: true,
            severity: 'success',
            message: translate('common:CHANGES_SAVED'),
        });
    };

    if (loading || !denthubSettings) {
        return <Loader />;
    }

    return (
        <Fragment>
            <Paper variant="outlined" sx={{ p: 3 }}>
                <TabContext value={tabState}>
                    <Box display="flex">
                        <Box display="flex">
                            <TabList
                                sx={{ minWidth: 250 }}
                                onChange={handleTabChange}
                                indicatorColor="primary"
                                textColor="primary"
                                orientation="vertical"
                            >
                                <SettingsTab label={translate('app-settings:APP_BANNER')} value="app-banner" />
                                <SettingsTab label={translate('app-settings:API_CLIENTS')} value="api-clients" />
                                <SettingsTab label={translate('app-settings:OAUTH_APPS')} value="oauth-apps" />
                                <SettingsTab
                                    label={translate('app-settings:CREDIFLOW_PARTY_LOOKUP')}
                                    value="crediflow-party-lookup"
                                />
                                {DB_SWITCH_ENABLED && (
                                    <SettingsTab label={translate('app-settings:DB_SWITCH_TAB')} value="db-switch" />
                                )}
                            </TabList>
                            <Box mx={3}>
                                <Divider orientation="vertical" />
                            </Box>
                        </Box>
                        <Box flexGrow={1}>
                            <TabPanel sx={{ padding: 0 }} value="app-banner">
                                <AppBannerSettings
                                    _id={denthubSettings._id}
                                    labAppBannerSettings={denthubSettings.labAppBanner}
                                    clinicAppBannerSettings={denthubSettings.clinicAppBanner}
                                    authAppBannerSettings={denthubSettings.authAppBanner}
                                    onSettingsSaved={handleSettingsUpdated}
                                />
                            </TabPanel>
                            <TabPanel sx={{ padding: 0 }} value="api-clients">
                                <ApiClientSettings />
                            </TabPanel>
                            <TabPanel sx={{ padding: 0 }} value="oauth-apps">
                                <OAuthApps />
                            </TabPanel>
                            <TabPanel sx={{ padding: 0 }} value="crediflow-party-lookup">
                                <CrediflowPartyLookup />
                            </TabPanel>
                            {DB_SWITCH_ENABLED && (
                                <TabPanel sx={{ padding: 0 }} value="db-switch">
                                    <DbSwitchSettings />
                                    <IntegrationApiModeSettings />
                                </TabPanel>
                            )}
                        </Box>
                    </Box>
                </TabContext>
            </Paper>
        </Fragment>
    );
};

interface SettingsTabProps {
    label: string;
    value: string;
    theme?: Theme;
}
const SettingsTab = styled((props: SettingsTabProps) => <Tab {...props} />)(({ theme }: { theme?: Theme }) => {
    return {
        color: theme?.palette.primary.main,
        maxWidth: 'none',
        '&.Mui-selected': {
            fontWeight: theme?.typography.fontWeightBold,
            background: theme?.palette.primary.light,
            color: '#fff',
        },
        '&:hover': {
            opacity: 0.8,
        },
        alignItems: 'start',
        textAlign: 'left',
    };
});
export default AppSettings;
